import { useMemo } from "react";
import type { UserDto } from "@fortex/api";
import { memoryLoader } from "@fortex/api";
import { Chip } from "@fortex/map";
import type { GridColumn } from "@fortex/map";
import { passwordProblems } from "../../account/AccountPage";
import { useApp, useAuth } from "../../app/context";
import { useLoad } from "../../app/useLoad";
import { features } from "../../auth/store";
import { CrudPage } from "../shared/CrudPage";
import type { FieldErrors } from "../shared/CrudPage";
import { UserForm, draftOf, saveUser } from "./UserForm";
import type { UserDraft } from "./UserForm";

export function userField(row: UserDto, field: string): unknown {
  switch (field) {
    case "FullName":
      return row.fullName || row.name || "";
    case "Email":
      return row.email ?? "";
    case "PhoneNumber":
      return row.phoneNumber ?? "";
    case "Admin":
      return row.admin;
    case "Active":
      return row.active;
    default:
      return row.id;
  }
}

export function UsersPage() {
  const { api, labels } = useApp();
  const canEdit = useAuth(a => a.can(features.userAdmin, "Full"));
  const canEditRoles = useAuth(a => a.can(features.role, "Full"));
  const users = useLoad(signal => api.users(null, null, signal), []);
  const rows = useMemo(() => (users.data ?? []).filter(a => a.type !== "ApiKey"), [users.data]);
  const load = useMemo(() => memoryLoader<UserDto>(rows, userField), [rows]);

  const columns = useMemo<GridColumn<UserDto>[]>(
    () => [
      { key: "FullName", header: labels.fullName, field: "FullName", sortable: true, filter: { kind: "text" }, render: row => row.fullName || row.name || "" },
      { key: "Email", header: labels.email, field: "Email", sortable: true, mono: true, filter: { kind: "text" }, render: row => row.email ?? "" },
      { key: "PhoneNumber", header: labels.phoneNumber, field: "PhoneNumber", sortable: true, filter: { kind: "text" }, render: row => row.phoneNumber ?? "" },
      { key: "Admin", header: labels.administrator, field: "Admin", sortable: true, width: "140px", filter: { kind: "boolean" }, render: row => (row.admin ? <Chip tone="accent">{labels.administrator}</Chip> : null) },
      { key: "Active", header: labels.userStatus, field: "Active", sortable: true, width: "120px", filter: { kind: "boolean" }, render: row => <Chip tone={row.active ? "success" : "neutral"}>{row.active ? labels.active : labels.inactive}</Chip> }
    ],
    [labels]
  );

  const validate = (draft: UserDraft, isNew: boolean): FieldErrors => {
    const errors: FieldErrors = {};

    if (draft.email.trim().length === 0) errors.email = labels.emailRequired;

    if (isNew) {
      const problem = passwordProblems(labels, draft.password, draft.confirmPassword);

      if (problem !== null) errors.password = problem;
    }

    return errors;
  };

  return (
    <CrudPage<UserDto, UserDraft>
      dataFxmap="users-page"
      columns={columns}
      rowKey={row => row.id}
      load={load}
      defaultSortField="FullName"
      quickFields={["FullName", "Email", "PhoneNumber"]}
      canEdit={canEdit}
      newLabel={labels.newUser}
      emptyDraft={() => draftOf(null)}
      toDraft={row => draftOf(row)}
      detailTitle={editing => (editing.isNew ? labels.createUser : labels.editUser)}
      detailSubtitle={editing => editing.draft.user?.email ?? null}
      form={(draft, setDraft, errors) => <UserForm api={api} labels={labels} draft={draft} setDraft={setDraft} errors={errors} canEditRoles={canEditRoles} readOnly={!canEdit} />}
      validate={validate}
      save={draft => saveUser(api, draft)}
      remove={canEdit ? async draft => (draft.user === null ? undefined : api.deleteUser(draft.user.id)) : null}
      deleteText={draft => labels.deleteUserText.replace("{name}", draft.fullName || draft.email)}
      savedText={labels.userUpdated}
      deletedText={labels.userDeleted}
      exportName="users"
      reloadToken={rows.length}
      onReload={users.reload}
    />
  );
}
